import {createEntityAdapter, createSlice} from "@reduxjs/toolkit";
import moment from "moment";
import {rxInit} from "./appSlice";
import {rxUpdateMileageLogs} from "./mileageLogsSlice";
import {rxUpdatePortfolioTransactions} from "./portfolioSlice";

export const PERIOD_TYPE_PERIOD = 0;
export const PERIOD_TYPE_YEAR = 1;
export const PERIOD_TYPE_MONTH = 2;
export const PERIOD_TYPE_WEEK = 3;
export const PERIOD_TYPE_LAST_7_DAYS = 4;
export const PERIOD_TYPE_LAST_30_DAYS = 5;
export const PERIOD_TYPE_LAST_90_DAYS = 6;
export const PERIOD_TYPE_LAST_365_DAYS = 7;
export const PERIOD_TYPE_ALL = 8;

export const DEFAULT_PERIOD_TYPE = PERIOD_TYPE_MONTH;

const YYYY_MM_DD = "YYYY-MM-DD";

export const PERIOD_TYPE = {
  [PERIOD_TYPE_YEAR]: {
    position: PERIOD_TYPE_YEAR,
    label: date => moment(date).format("YYYY"),
    chooserLabel: t => t("period_chooser_year"),
    getPreviousStart: date => moment(date).subtract(1, "year").format(YYYY_MM_DD),
    getNextStart: date => moment(date).add(1, "year").format(YYYY_MM_DD),
    getNextStartExact: date => moment(date).startOf("year").add(1, "year").format(YYYY_MM_DD),
    getTransactions: (transactions, date) => {
      const startValue = moment(date).startOf("year").format(YYYY_MM_DD);
      const endValue = moment(date).endOf("year").format(YYYY_MM_DD);

      return transactions.filter(t => t.valueDate >= startValue && t.valueDate <= endValue);
    }
  },
  [PERIOD_TYPE_MONTH]: {
    position: PERIOD_TYPE_MONTH,
    label: date => moment(date).format("MMMM YYYY"),
    chooserLabel: t => t("period_chooser_month"),
    getPreviousStart: date => moment(date).subtract(1, "month").format(YYYY_MM_DD),
    getNextStart: date => moment(date).add(1, "month").format(YYYY_MM_DD),
    getNextStartExact: date => moment(date).startOf("month").add(1, "month").format(YYYY_MM_DD),
    getTransactions: (transactions, date) => {
      const startValue = moment(date).startOf("month").format(YYYY_MM_DD);
      const endValue = moment(date).endOf("month").format(YYYY_MM_DD);

      return transactions.filter(t => t.valueDate >= startValue && t.valueDate <= endValue);
    }
  },
  [PERIOD_TYPE_WEEK]: {
    position: PERIOD_TYPE_WEEK,
    label: date => moment(date).startOf("week").format("DD MMM YY") + " - " + moment(date).startOf("week").add(6, "day").format("DD MMM YY"),
    chooserLabel: t => t("period_chooser_week"),
    getPreviousStart: date => moment(date).subtract(1, "week").format(YYYY_MM_DD),
    getNextStart: date => moment(date).add(1, "week").format(YYYY_MM_DD),
    getNextStartExact: date => moment(date).startOf("week").add(1, "week").format(YYYY_MM_DD),
    getTransactions: (transactions, date) => {
      const startValue = moment(date).startOf("week").format(YYYY_MM_DD);
      const endValue = moment(date).endOf("week").format(YYYY_MM_DD);

      return transactions.filter(t => t.valueDate >= startValue && t.valueDate <= endValue);
    }
  },
  [PERIOD_TYPE_LAST_7_DAYS]: {
    position: PERIOD_TYPE_LAST_7_DAYS,
    label: (_, t) => t("period_last_7_days"),
    chooserLabel: t => t("period_chooser_last_7_days"),
    isPeriod: true,
    getPreviousStart: null,
    getNextStart: null,
    getTransactions: transactions => {
      const startValue = moment().subtract(7, "days").format(YYYY_MM_DD);
      const endValue = moment().format(YYYY_MM_DD);

      return transactions.filter(t => t.valueDate >= startValue && t.valueDate <= endValue);
    }
  },
  [PERIOD_TYPE_LAST_30_DAYS]: {
    position: PERIOD_TYPE_LAST_30_DAYS,
    label: (_, t) => t("period_last_30_days"),
    chooserLabel: t => t("period_chooser_last_30_days"),
    isPeriod: true,
    getPreviousStart: null,
    getNextStart: null,
    getTransactions: transactions => {
      const startValue = moment().subtract(30, "days").format(YYYY_MM_DD);
      const endValue = moment().format(YYYY_MM_DD);

      return transactions.filter(t => t.valueDate >= startValue && t.valueDate <= endValue);
    }
  },
  [PERIOD_TYPE_LAST_90_DAYS]: {
    position: PERIOD_TYPE_LAST_90_DAYS,
    label: (_, t) => t("period_last_90_days"),
    chooserLabel: t => t("period_chooser_last_90_days"),
    isPeriod: true,
    getPreviousStart: null,
    getNextStart: null,
    getTransactions: transactions => {
      const startValue = moment().subtract(90, "days").format(YYYY_MM_DD);
      const endValue = moment().format(YYYY_MM_DD);

      return transactions.filter(t => t.valueDate >= startValue && t.valueDate <= endValue);
    }
  },
  [PERIOD_TYPE_LAST_365_DAYS]: {
    position: PERIOD_TYPE_LAST_365_DAYS,
    label: (_, t) => t("period_last_365_days"),
    chooserLabel: t => t("period_chooser_last_365_days"),
    isPeriod: true,
    getPreviousStart: null,
    getNextStart: null,
    getTransactions: transactions => {
      const startValue = moment().subtract(365, "days").format(YYYY_MM_DD);
      const endValue = moment().format(YYYY_MM_DD);

      return transactions.filter(t => t.valueDate >= startValue && t.valueDate <= endValue);
    }
  },
  [PERIOD_TYPE_ALL]: {
    position: PERIOD_TYPE_ALL,
    label: (_, t) => t("period_all"),
    chooserLabel: t => t("period_chooser_all"),
    isPeriod: true,
    getPreviousStart: null,
    getNextStart: null,
    getTransactions: transactions => transactions
  }
};

const transactionsAdapter = createEntityAdapter({
  sortComparer: (a, b) => {
    if (a.future && !b.future) {
      return -1;
    } else if (!a.future && b.future) {
      return 1;
    }

    if (b.valueDate > a.valueDate) {
      return 1;
    } else if (b.valueDate < a.valueDate) {
      return -1;
    }

    return b.createTime.localeCompare(a.createTime);
  }
});

const initialState = {
  transactions: transactionsAdapter.getInitialState(),

  list: {
    listKey: 0,
    periodType: DEFAULT_PERIOD_TYPE,
    periodStart: moment().format(YYYY_MM_DD),
    inactiveAccounts: false,
    selectedTransactions: {},
    selectedAccounts: {},
    selectedCategories: {},
    searchValue: ""
  },

  chooseCategoryModal: {show: false},
  addTagsModal: {show: false},
  removeTagsModal: {show: false},
  removeTransactionsModal: {show: false},
  transfertAssociationModal: {show: false, impossible: false},

  editor: {
    show: false,
    mode: null,
    deleteConfirmationModal: false
  }
};

export const transactionsSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    choosePeriod: (state, action) => {
      state.list.listKey++;
      state.list.selectedTransactions = {};
      state.list.periodType = action.payload.periodType;
      state.list.periodStart = action.payload.periodStart;
    },

    search: (state, action) => {
      state.list.listKey++;
      state.list.selectedTransactions = {};
      state.list.searchValue = action.payload.searchValue;
    },

    toggleInactiveAccounts: (state) => {
      state.list.inactiveAccounts = !state.list.inactiveAccounts;
    },

    toggleAllTransactions: (state, action) => {
      state.list.selectedTransactions = action.payload.selectedTransactions;
    },

    toggleSelectedTransaction: (state, action) => {
      const id = action.payload.transaction.id;

      if (state.list.selectedTransactions[id]) {
        delete state.list.selectedTransactions[id];
      } else {
        state.list.selectedTransactions[id] = true;
      }
    },

    toggleSelectedAccount: (state, action) => {
      state.list.listKey++;
      state.list.selectedTransactions = {};

      const id = action.payload.account.id;

      if (state.list.selectedAccounts[id] === undefined) {
        state.list.selectedAccounts[id] = false;
      } else {
        state.list.selectedAccounts[id] = !state.list.selectedAccounts[id];
      }
    },

    toggleSelectedCategory: (state, action) => {
      state.list.listKey++;
      state.list.selectedTransactions = {};

      const id = action.payload.category.id;

      if (state.list.selectedCategories[id] === undefined) {
        state.list.selectedCategories[id] = false;
      } else {
        state.list.selectedCategories[id] = !state.list.selectedCategories[id];
      }
    },

    toggleAllCategories: (state, action) => {
      state.list.listKey++;
      state.list.selectedTransactions = {};
      state.list.selectedCategories = action.payload.selectedCategories;
    },

    showEditor: (state, action) => {
      state.editor.show = true;
      state.editor.mode = action.payload.mode;
      state.editor.transaction = action.payload.transaction;
      state.editor.deleteConfirmationModal = false;
    },

    hideEditor: state => {
      state.editor.show = false;
      state.editor.deleteConfirmationModal = false;
    },

    showHideDeleteConfirmationModal: (state, action) => {
      state.editor.deleteConfirmationModal = action.payload;
    },

    updateTransactions: (state, action) => {
      transactionsAdapter.setMany(state.transactions, action.payload.utransactions || []);
      transactionsAdapter.removeMany(state.transactions, action.payload.dtransactions || []);

      (action.payload.dtransactions || []).forEach(t => delete state.list.selectedTransactions[t]);

      state.list.listKey++;
    },

    showChooseCategoryModal: (state, action) => {
      state.chooseCategoryModal.show = action.payload;
    },

    showAddTagsModal: (state, action) => {
      state.addTagsModal.show = action.payload;
    },

    showRemoveTagsModal: (state, action) => {
      state.removeTagsModal.show = action.payload;
    },

    showRemoveTransactionsModal: (state, action) => {
      state.removeTransactionsModal.show = action.payload;
    },

    showTransfertAssociationModal: (state, action) => {
      state.transfertAssociationModal.show = action.payload.show;
      state.transfertAssociationModal.impossible = action.payload.impossible || false;
    }
  },
  extraReducers: builder => {
    builder.addCase(rxInit, (state, action) => {
      transactionsAdapter.setMany(state.transactions, action.payload.transactions);

      state.list.periodType = action.payload.defaultTransactionsPeriod;
    });

    builder.addCase(rxUpdateMileageLogs, (state, action) => {
      transactionsAdapter.setMany(state.transactions, action.payload.utransactions || []);
      transactionsAdapter.removeMany(state.transactions, action.payload.dtransactions || []);

      (action.payload.dtransactions || []).forEach(t => delete state.list.selectedTransactions[t]);

      state.list.listKey++;
    });

    builder.addCase(rxUpdatePortfolioTransactions, (state, action) => {
      transactionsAdapter.setMany(state.transactions, action.payload.utransactions || []);
      transactionsAdapter.removeMany(state.transactions, action.payload.dtransactions || []);

      (action.payload.dtransactions || []).forEach(t => delete state.list.selectedTransactions[t]);

      state.list.listKey++;
    });
  }
});

export const {
  choosePeriod: rxChoosePeriod,
  toggleInactiveAccounts: rxToggleInactiveAccounts,
  toggleAllTransactions: rxToggleAllTransactions,
  toggleSelectedTransaction: rxToggleSelectedTransaction,
  toggleSelectedAccount: rxToggleSelectedAccount,
  toggleSelectedCategory: rxToggleSelectedCategory,
  toggleAllCategories: rxToggleAllCategories,
  search: rxSearch,
  showEditor: rxShowTransactionEditor,
  hideEditor: rxHideTransactionEditor,
  updateTransactions: rxUpdateTransactions,
  showHideDeleteConfirmationModal: rxShowHideDeleteConfirmationModal,

  showChooseCategoryModal: rxShowChooseCategoryModal,
  showAddTagsModal: rxShowAddTagsModal,
  showRemoveTagsModal: rxShowRemoveTagsModal,
  showRemoveTransactionsModal: rxShowRemoveTransactionsModal,
  showTransfertAssociationModal: rxShowTransfertAssociationModal,
} = transactionsSlice.actions;

export const {
  selectAll: selectAllTransactions,
  selectById: selectTransactionById
} = transactionsAdapter.getSelectors(state => state.transactions.transactions);

export const listConfigSelector = state => state.transactions.list;
export const editorConfigSelector = state => state.transactions.editor;
export const chooseCategoryModalSelector = state => state.transactions.chooseCategoryModal;
export const addTagsModalSelector = state => state.transactions.addTagsModal;
export const removeTagsModalSelector = state => state.transactions.removeTagsModal;
export const removeTransactionsModalSelector = state => state.transactions.removeTransactionsModal;
export const transfertAssociationModalSelector = state => state.transactions.transfertAssociationModal;

export default transactionsSlice.reducer;
